import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  LinearProgress,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Backup,
  CloudDownload,
  Delete,
  Restore,
  Add,
  Storage,
  CheckCircle,
  Schedule,
  Refresh,
} from "@mui/icons-material";
import axios from "axios";
import logger from "../../utils/logger";

const Backups = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [actionType, setActionType] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchBackups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBackups = async () => {
    setLoading(true);
    const token = localStorage.getItem("superAdminToken");
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5000/api"
        }/super-admin/backups`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBackups(response.data.data);
    } catch (error) {
      logger.error("Error fetching backups:", error);
      showSnackbar("فشل تحميل النسخ الاحتياطية", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    const token = localStorage.getItem("superAdminToken");
    setLoading(true);
    try {
      await axios.post(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5000/api"
        }/super-admin/backups`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("تم إنشاء النسخة الاحتياطية بنجاح", "success");
      fetchBackups();
      setCreateDialog(false);
    } catch (error) {
      logger.error("Error creating backup:", error);
      showSnackbar("فشل إنشاء النسخة الاحتياطية", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreBackup = async () => {
    const token = localStorage.getItem("superAdminToken");
    setLoading(true);
    try {
      await axios.post(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5000/api"
        }/super-admin/backups/${selectedBackup.id}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("تم استعادة النسخة الاحتياطية بنجاح", "success");
      setConfirmDialog(false);
    } catch (error) {
      logger.error("Error restoring backup:", error);
      showSnackbar("فشل استعادة النسخة الاحتياطية", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBackup = async () => {
    const token = localStorage.getItem("superAdminToken");
    try {
      await axios.delete(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5000/api"
        }/super-admin/backups/${selectedBackup.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("تم حذف النسخة الاحتياطية بنجاح", "success");
      fetchBackups();
      setConfirmDialog(false);
    } catch (error) {
      logger.error("Error deleting backup:", error);
      showSnackbar("فشل حذف النسخة الاحتياطية", "error");
    }
  };

  const openConfirmDialog = (backup, action) => {
    setSelectedBackup(backup);
    setActionType(action);
    setConfirmDialog(true);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "in_progress":
        return "info";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "مكتملة";
      case "in_progress":
        return "قيد الإنشاء";
      case "failed":
        return "فشلت";
      default:
        return status;
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          النسخ الاحتياطية
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchBackups}
          >
            تحديث
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialog(true)}
          >
            إنشاء نسخة احتياطية
          </Button>
        </Box>
      </Box>

      {/* Info Card */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    إجمالي النسخ
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {backups.length}
                  </Typography>
                </Box>
                <Storage
                  sx={{ fontSize: 50, color: "primary.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    النسخ المكتملة
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {backups.filter((b) => b.status === "completed").length}
                  </Typography>
                </Box>
                <CheckCircle
                  sx={{ fontSize: 50, color: "success.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    آخر نسخة احتياطية
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {backups.length > 0
                      ? new Date(backups[0].created_at).toLocaleDateString(
                          "ar-IQ"
                        )
                      : "لا توجد"}
                  </Typography>
                </Box>
                <Schedule
                  sx={{ fontSize: 50, color: "info.main", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>ملاحظة:</strong> يتم إنشاء نسخ احتياطية تلقائية يومياً. يمكنك
          أيضاً إنشاء نسخة احتياطية يدوية في أي وقت. يوصى بالاحتفاظ بالنسخ
          الاحتياطية في مكان آمن خارج الخادم.
        </Typography>
      </Alert>

      {/* Table */}
      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>اسم الملف</strong>
                </TableCell>
                <TableCell>
                  <strong>الحجم</strong>
                </TableCell>
                <TableCell>
                  <strong>النوع</strong>
                </TableCell>
                <TableCell>
                  <strong>التاريخ</strong>
                </TableCell>
                <TableCell>
                  <strong>الحالة</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>الإجراءات</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box py={5}>
                      <Backup
                        sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
                      />
                      <Typography color="text.secondary">
                        لا توجد نسخ احتياطية
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setCreateDialog(true)}
                        sx={{ mt: 2 }}
                      >
                        إنشاء نسخة احتياطية الآن
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                backups.map((backup) => (
                  <TableRow key={backup.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Storage color="primary" />
                        <Typography variant="body2">
                          {backup.file_name || `backup_${backup.id}.sql`}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatFileSize(backup.file_size)}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          backup.backup_type === "automatic" ? "تلقائي" : "يدوي"
                        }
                        size="small"
                        color={
                          backup.backup_type === "automatic"
                            ? "primary"
                            : "secondary"
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(backup.created_at).toLocaleString("ar-IQ", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(backup.status)}
                        color={getStatusColor(backup.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {backup.status === "completed" && (
                        <>
                          <Tooltip title="استعادة">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                openConfirmDialog(backup, "restore")
                              }
                            >
                              <Restore />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="تنزيل">
                            <IconButton size="small" color="info">
                              <CloudDownload />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="حذف">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                openConfirmDialog(backup, "delete")
                              }
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)}>
        <DialogTitle>إنشاء نسخة احتياطية جديدة</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2 }}>
            سيتم إنشاء نسخة احتياطية كاملة لجميع قواعد البيانات. قد تستغرق هذه
            العملية بضع دقائق.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>إلغاء</Button>
          <Button
            onClick={handleCreateBackup}
            variant="contained"
            disabled={loading}
          >
            {loading ? "جاري الإنشاء..." : "إنشاء"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>
          {actionType === "restore" ? "تأكيد الاستعادة" : "تأكيد الحذف"}
        </DialogTitle>
        <DialogContent>
          {actionType === "restore" ? (
            <Alert severity="warning">
              <Typography>
                هل أنت متأكد من استعادة هذه النسخة الاحتياطية؟
                <br />
                <strong>تحذير:</strong> سيتم استبدال البيانات الحالية بالبيانات
                من النسخة الاحتياطية. هذا الإجراء لا يمكن التراجع عنه.
              </Typography>
            </Alert>
          ) : (
            <Typography>
              هل أنت متأكد من حذف النسخة الاحتياطية "{selectedBackup?.file_name}
              "؟ هذا الإجراء لا يمكن التراجع عنه.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>إلغاء</Button>
          <Button
            onClick={
              actionType === "restore"
                ? handleRestoreBackup
                : handleDeleteBackup
            }
            color={actionType === "restore" ? "primary" : "error"}
            variant="contained"
          >
            تأكيد
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Backups;
